import React, { useCallback, useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react';
import { CNav, CNavItem, CNavLink, CTabContent, CTabPane } from '@coreui/react-pro';
import { useHistory, useLocation } from 'react-router-dom';
import KpiSummary from './components/KpiSummary';
import FiltersBar from './components/FiltersBar';
import CARReportsTable from './components/CARReportsTable';
import { useCarSearchAndFilter, ALL_VALUE } from './hooks/useCarSearchAndFilter';
import Access from '../../components/Access';
import { AccessType } from '../../constants/roleAccessConstant';
import { CXButton } from '../../components/CXForm';

import type { ICarReport, CarReportApi } from './model/carReport.types';
import { mapCarReportApiToUI } from './model/carReport.mappers';

import { getCarReports, CarSearchParams, normalizeCarReportsResponse, getAvgCaseTime, getCarReportFilterOptions } from '../../services/carReportsAPI';
import { REPORTS_PATHS } from '../../constants/path';
import CARReportTrackerAssigneePage from './CARReportTrackerAssigneePage';
import TablePagination from '../../components/CXForm/TablePagination';
import './style/CARReportPage.scss';

type SortOrder = 'asc' | 'desc';
type BackendSortBy =
    | 'ufi'
    | 'category'
    | 'subject'
    | 'creator'
    | 'assigneeLead'
    | 'assigneeGroup'
    | 'status'
    | 'createdAt';

const AssigneeTab: React.FC = () => (
    <div style={{ paddingTop: 8 }}>
        <CARReportTrackerAssigneePage />
    </div>
);

const BASE_PARAMS: CarSearchParams = { ufi: '', comment: '' };

const isBaseParams = (p: CarSearchParams) => !(p.ufi || '').trim() && !(p.comment || '').trim();


const STORAGE_LIMIT_KEY = 'carReportTracker.limit';
const STORAGE_LIST_STATE_KEY = 'carReportTracker.listState';

const STORAGE_RESTORE_ON_NEXT_MOUNT_KEY = 'carReportTracker.restoreOnNextMount';
const STORAGE_TAB_KEY = 'carReportTracker.tabKey';

const isHardReload = () => {
    try {
        const nav = (performance.getEntriesByType?.('navigation')?.[0] as any) || null;
        if (nav?.type) return nav.type === 'reload';
        const legacy = (performance as any)?.navigation;
        return legacy?.type === 1;
    } catch {
        return false;
    }
};

const clearAssigneePageStates = () => {
    try {
        const prefix = 'carReportTracker.assigneePageState:';
        for (let i = sessionStorage.length - 1; i >= 0; i--) {
            const k = sessionStorage.key(i);
            if (k && k.startsWith(prefix)) sessionStorage.removeItem(k);
        }
    } catch {
        // ignore
    }
};

const readPersistedLimit = (fallback: number) => {
    try {
        const raw = localStorage.getItem(STORAGE_LIMIT_KEY);
        const n = raw ? Number(raw) : NaN;
        return Number.isFinite(n) && n > 0 ? n : fallback;
    } catch {
        return fallback;
    }
};

const writePersistedLimit = (value: number) => {
    try {
        localStorage.setItem(STORAGE_LIMIT_KEY, String(value));
    } catch {
        // ignore
    }
};

type PersistedCarListState = {
    tabKey: number;
    page: number;
    limit: number;
    totalPages: number;

    lastSearchParams: CarSearchParams;
    didSearch: boolean;

    searchUFI: string;
    searchComment: string;

    origin: string;
    dest: string;
    category: string;
    subject: string;
    creator: string;
    assigneeLead: string;
    assigneeGroup: string;
    status: string;

    sortBy?: BackendSortBy;
    sortOrder?: SortOrder;
};

const readPersistedCarListState = (): PersistedCarListState | null => {
    try {
        const raw = sessionStorage.getItem(STORAGE_LIST_STATE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed) return null;

        const tabKey = Number(parsed.tabKey);
        const page = Number(parsed.page ?? 1);
        const limit = Number(parsed.limit);
        const totalPages = Number(parsed.totalPages);

        const didSearch = Boolean(parsed.didSearch);
        const lastSearchParams: CarSearchParams = parsed.lastSearchParams || { ufi: '', comment: '' };

        const searchUFI = String(parsed.searchUFI ?? '');
        const searchComment = String(parsed.searchComment ?? '');

        const origin = String(parsed.origin ?? '');
        const dest = String(parsed.dest ?? '');
        const category = String(parsed.category ?? '');
        const subject = String(parsed.subject ?? '');
        const creator = String(parsed.creator ?? '');
        const assigneeLead = String(parsed.assigneeLead ?? '');
        const assigneeGroup = String(parsed.assigneeGroup ?? '');
        const status = String(parsed.status ?? '');

        const sortBy = parsed.sortBy as BackendSortBy | undefined;
        const sortOrder = parsed.sortOrder as SortOrder | undefined;

        if (!Number.isFinite(tabKey) || tabKey < 0) return null;
        if (!Number.isFinite(page) || page < 1) return null;
        if (!Number.isFinite(limit) || limit < 1) return null;

        return {
            tabKey,
            page,
            limit,
            totalPages: Number.isFinite(totalPages) && totalPages > 0 ? totalPages : 1,
            lastSearchParams,
            didSearch,
            searchUFI,
            searchComment,
            origin,
            dest,
            category,
            subject,
            creator,
            assigneeLead,
            assigneeGroup,
            status,
            sortBy,
            sortOrder,
        };
    } catch {
        return null;
    }
};

const writePersistedCarListState = (state: PersistedCarListState) => {
    try {
        sessionStorage.setItem(STORAGE_LIST_STATE_KEY, JSON.stringify(state));
    } catch {
        // ignore
    }
};

const isActiveValue = (v: any) => {
    const s = String(v ?? '').trim();
    if (!s) return false;
    return s !== ALL_VALUE && s.toUpperCase() !== 'ALL';
};

type CarReportFilterOptionsData = {
    origin: string[];
    dest: string[];
    category: string[];
    subject: string[];
    creator: string[];
    assigneeLead: string[];
    assigneeGroup: string[];
    status: string[];
};

const EMPTY_FILTER_OPTIONS: CarReportFilterOptionsData = {
    origin: [],
    dest: [],
    category: [],
    subject: [],
    creator: [],
    assigneeLead: [],
    assigneeGroup: [],
    status: [],
};

const CARReportTrackerReportPage: React.FC = () => {
    const history = useHistory();

    useEffect(() => {
        const resetPageBeforeReload = () => {
            try {
                const raw = sessionStorage.getItem(STORAGE_LIST_STATE_KEY);
                if (!raw) return;

                const parsed = JSON.parse(raw);
                if (!parsed) return;

                sessionStorage.setItem(
                    STORAGE_LIST_STATE_KEY,
                    JSON.stringify({
                        ...parsed,
                        page: 1,
                    }),
                );
            } catch {
                // ignore
            }
        };

        window.addEventListener('beforeunload', resetPageBeforeReload);
        window.addEventListener('pagehide', resetPageBeforeReload);

        return () => {
            window.removeEventListener('beforeunload', resetPageBeforeReload);
            window.removeEventListener('pagehide', resetPageBeforeReload);
        };
    }, []);

    const location = useLocation<any>();
    const routePath = location.pathname;

    // ----- bootstrap (decide once) -----
    //- Refresh: Clear (Report+Assign)
    //- Switch back to the left menu (normal navigation in): Clear (Report+Assign)
    //- Return from detail: Do not clear (relying on restore flag)
    const bootRef = useRef<{
        allowRestore: boolean;
        shouldClear: boolean;
        restored: PersistedCarListState | null;
    } | null>(null);

    if (!bootRef.current) {
        let allowRestoreNow = false;

        try {
            const flag = sessionStorage.getItem(STORAGE_RESTORE_ON_NEXT_MOUNT_KEY) === '1';

            // POP: browser back/goBack -> treat as coming back
            const byPop = history.action === 'POP';

            // PUSH: breadcrumb back -> we mark it via location.state
            const byBreadcrumb = Boolean(location?.state?.__fromDetail);

            // Only restore when the flag exists AND (POP back OR breadcrumb-marked PUSH)
            allowRestoreNow = flag && (byPop || byBreadcrumb);

            const shouldClearNow = !allowRestoreNow;

            if (shouldClearNow) {
                sessionStorage.removeItem(STORAGE_LIST_STATE_KEY);
                clearAssigneePageStates();

                // important: remove stale flag so it won't incorrectly affect later navigation
                sessionStorage.removeItem(STORAGE_RESTORE_ON_NEXT_MOUNT_KEY);
                if (!isHardReload()) {
                    try {
                        localStorage.setItem(STORAGE_TAB_KEY, '0');
                    } catch {
                        // ignore
                    }
                }
            }

            const restoredNow = allowRestoreNow ? readPersistedCarListState() : null;

            bootRef.current = {
                allowRestore: allowRestoreNow,
                shouldClear: shouldClearNow,
                restored: restoredNow,
            };
        } catch {
            // If anything fails, default to "clear"
            try {
                sessionStorage.removeItem(STORAGE_LIST_STATE_KEY);
                clearAssigneePageStates();
                sessionStorage.removeItem(STORAGE_RESTORE_ON_NEXT_MOUNT_KEY);
            } catch {
                // ignore
            }

            bootRef.current = {
                allowRestore: false,
                shouldClear: true,
                restored: null,
            };
        }
    }

    const allowRestore = bootRef.current.allowRestore;
    const shouldClear = bootRef.current.shouldClear;
    const restored = bootRef.current.restored;

    console.log('[CARTracker] action=', history.action, 'allowRestore=', allowRestore, 'shouldClear=', shouldClear);

    // consume flag after mount (and avoid re-render side effects)
    useEffect(() => {
        if (!allowRestore) return;

        // delay removal to avoid odd dev/strict-mode behavior
        const t = window.setTimeout(() => {
            try {
                sessionStorage.removeItem(STORAGE_RESTORE_ON_NEXT_MOUNT_KEY);
            } catch {
                // ignore
            }
        }, 0);

        return () => window.clearTimeout(t);
    }, [allowRestore]);

    // ----- KPI -----
    const [avgCompletionDays, setAvgCompletionDays] = useState(0);
    const [newTodayCount, setNewTodayCount] = useState(0);
    const [inProgressCount, setInProgressCount] = useState(0);

    // ----- UI state -----
    const readPersistedTabKey = (fallback: number) => {
        try {
            const raw = localStorage.getItem(STORAGE_TAB_KEY);
            const n = raw ? Number(raw) : NaN;
            return Number.isFinite(n) && (n === 0 || n === 1) ? n : fallback;
        } catch {
            return fallback;
        }
    };

    const [tabKey, setTabKey] = useState(() => {
        if (Number.isFinite(restored?.tabKey)) return restored!.tabKey;
        return readPersistedTabKey(0);
    });
    const [hasVisitedAssigneeTab, setHasVisitedAssigneeTab] = useState(() => {
        if (Number.isFinite(restored?.tabKey)) return restored!.tabKey === 1;
        return readPersistedTabKey(0) === 1;
    });

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_TAB_KEY, String(tabKey));
        } catch {
            // ignore
        }
    }, [tabKey]);

    useEffect(() => {
        if (tabKey === 1) {
            setHasVisitedAssigneeTab(true);
        }
    }, [tabKey]);

    const [page, setPage] = useState(() => restored?.page ?? 1);
    const [limit, setLimit] = useState(() => {
        const n = restored?.limit;
        if (Number.isFinite(n) && (n as number) > 0) return n as number;
        return readPersistedLimit(25);
    });

    const [totalPages, setTotalPages] = useState(() => restored?.totalPages ?? 1);

    const [uiSearchUFI, setUiSearchUFI] = useState(() => restored?.searchUFI ?? '');
    const [uiSearchComment, setUiSearchComment] = useState(() => restored?.searchComment ?? '');

    const [sortBy, setSortBy] = useState<BackendSortBy>(() => restored?.sortBy ?? 'createdAt');
    const [sortOrder, setSortOrder] = useState<SortOrder>(() => restored?.sortOrder ?? 'desc');

    const [searchParams, setSearchParams] = useState<CarSearchParams>(() => {
        const p = restored?.lastSearchParams;
        if (p && !isBaseParams(p)) return p;

        return BASE_PARAMS;
    });

    // ----- data lists -----
    const [serverPageList, setServerPageList] = useState<ICarReport[]>([]);

    // Fixed full filter options (NOT dynamic based on table data)
    const [fixedFilterOptions, setFixedFilterOptions] = useState<CarReportFilterOptionsData>(EMPTY_FILTER_OPTIONS);

    // ----- refs: cancel / avoid duplicates -----
    const didInitReportMetaRef = useRef(false);
    const serverSeqRef = useRef(0);

    const prevUiHasAnyRef = useRef(Boolean((restored?.searchUFI || '').trim() || (restored?.searchComment || '').trim()));

    const onViewReport = useCallback(
        (report: ICarReport) => {
            try {
                sessionStorage.setItem(STORAGE_RESTORE_ON_NEXT_MOUNT_KEY, '1');
            } catch {
                // ignore
            }
            const to = REPORTS_PATHS.carReportDetail.replace(':ufi', encodeURIComponent(report.ufi || ''));
            history.push(to, { report });
        },
        [history],
    );

    // ----- safe wrappers -----
    const safeGet = useCallback(async (payload: any) => {
        try {
            return await getCarReports(payload);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('[CARReportTracker] getCarReports failed:', e, payload);
            return null;
        }
    }, []);

    const safeNormalize = useCallback((res: any, fallback: { page: number; limit: number }) => {
        try {
            return normalizeCarReportsResponse(res);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('[CARReportTracker] normalizeCarReportsResponse failed:', e, res);
            return { code: -1, records: [], page: fallback.page, limit: fallback.limit, totalPages: 1 };
        }
    }, []);

    const avgSeqRef = useRef(0);
    const fetchAvgCompletionDaysFromApi = useCallback(async () => {
        const seq = ++avgSeqRef.current;
        try {
            const res = await getAvgCaseTime();
            if (seq !== avgSeqRef.current) return;

            const payload = (res as any)?.data ?? res;

            if (res?.code !== 0) return;

            const avg = payload?.averageCompletionDays;
            const createdToday = payload?.createdTodayCount;
            const inProgress = payload?.inProgressCount;

            if (Number.isFinite(avg)) setAvgCompletionDays(Math.round(Number(avg)));
            if (Number.isFinite(createdToday)) setNewTodayCount(createdToday);
            if (Number.isFinite(inProgress)) setInProgressCount(inProgress);

        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('[CARReportTracker] getAvgCaseTime failed:', e);
        }
    }, []);

    // server fetch now includes filters in params (origin/dest/...)
    const fetchServerPage = useCallback(
        async (params: CarSearchParams, reqPage: number, reqLimit: number, by: BackendSortBy, order: SortOrder) => {
            const seq = ++serverSeqRef.current;

            const res = await safeGet({ ...params, page: reqPage, limit: reqLimit, sortBy: by, sortOrder: order } as any);
            if (seq !== serverSeqRef.current) return;

            const normalized = safeNormalize(res, { page: reqPage, limit: reqLimit });
            if (normalized.code !== 0) {
                setServerPageList([]);
                setPage(1);
                setTotalPages(1);
                return;
            }

            const list = ((normalized.records || []) as CarReportApi[]).map(mapCarReportApiToUI);
            setServerPageList(list);

            setPage(normalized.page);
            setLimit(normalized.limit);
            writePersistedLimit(normalized.limit);
            setTotalPages(normalized.totalPages || 1);
        },
        [safeGet, safeNormalize],
    );

    // Fetch fixed full filter options on tracker page enter
    const filterOptSeqRef = useRef(0);
    const fetchFilterOptionsFromApi = useCallback(async () => {
        const seq = ++filterOptSeqRef.current;
        try {
            const res = await getCarReportFilterOptions();
            if (seq !== filterOptSeqRef.current) return;

            if (res?.code === 0 && res?.data) {
                setFixedFilterOptions({
                    origin: Array.isArray(res.data.origin) ? res.data.origin : [],
                    dest: Array.isArray(res.data.dest) ? res.data.dest : [],
                    category: Array.isArray(res.data.category) ? res.data.category : [],
                    subject: Array.isArray(res.data.subject) ? res.data.subject : [],
                    creator: Array.isArray(res.data.creator) ? res.data.creator : [],
                    assigneeLead: Array.isArray(res.data.assigneeLead) ? res.data.assigneeLead : [],
                    assigneeGroup: Array.isArray(res.data.assigneeGroup) ? res.data.assigneeGroup : [],
                    status: Array.isArray(res.data.status) ? res.data.status : [],
                });
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('[CARReportTracker] getCarReportFilterOptions failed:', e);
        }
    }, []);

    // ----- derived: applied search -----
    const isSearchApplied = useMemo(() => !isBaseParams(searchParams), [searchParams]);

    // ----- init filters from restored -----
    const initialFilters = useMemo(() => {
        if (!restored) return undefined;
        return {
            origin: restored.origin,
            dest: restored.dest,
            category: restored.category,
            subject: restored.subject,
            creator: restored.creator,
            assigneeLead: restored.assigneeLead,
            assigneeGroup: restored.assigneeGroup,
            status: restored.status,
        };
    }, [restored]);

    // Use fixed full options (3rd param fixedFilterOptions)
    const {
        origin,
        setOrigin: setOriginState,
        dest,
        setDest: setDestState,
        category,
        setCategory: setCategoryState,
        subject,
        setSubject: setSubjectState,
        creator,
        setCreator: setCreatorState,
        assigneeLead,
        setAssigneeLead: setAssigneeLeadState,
        assigneeGroup,
        setAssigneeGroup: setAssigneeGroupState,
        status,
        setStatus: setStatusState,
        options,
    } = useCarSearchAndFilter(serverPageList, initialFilters, fixedFilterOptions);

    // Reset to page=1 when selecting any filter (wrap each setter)
    const setOrigin = useCallback(
        (v: string) => {
            setPage(1);
            setOriginState(v);
        },
        [setOriginState],
    );
    const setDest = useCallback(
        (v: string) => {
            setPage(1);
            setDestState(v);
        },
        [setDestState],
    );
    const setCategory = useCallback(
        (v: string) => {
            setPage(1);
            setCategoryState(v);
        },
        [setCategoryState],
    );
    const setSubject = useCallback(
        (v: string) => {
            setPage(1);
            setSubjectState(v);
        },
        [setSubjectState],
    );
    const setCreator = useCallback(
        (v: string) => {
            setPage(1);
            setCreatorState(v);
        },
        [setCreatorState],
    );
    const setAssigneeLead = useCallback(
        (v: string) => {
            setPage(1);
            setAssigneeLeadState(v);
        },
        [setAssigneeLeadState],
    );
    const setAssigneeGroup = useCallback(
        (v: string) => {
            setPage(1);
            setAssigneeGroupState(v);
        },
        [setAssigneeGroupState],
    );
    const setStatus = useCallback(
        (v: string) => {
            setPage(1);
            setStatusState(v);
        },
        [setStatusState],
    );

    const hasActiveFilters = useMemo(() => {
        return (
            isActiveValue(origin) ||
            isActiveValue(dest) ||
            isActiveValue(category) ||
            isActiveValue(subject) ||
            isActiveValue(creator) ||
            isActiveValue(assigneeLead) ||
            isActiveValue(assigneeGroup) ||
            isActiveValue(status)
        );
    }, [origin, dest, category, subject, creator, assigneeLead, assigneeGroup, status]);

    const hasAnyFilterSelection = useMemo(() => {
        const hasValue = (v: any) => String(v ?? '').trim().length > 0;
        return (
            hasValue(origin) ||
            hasValue(dest) ||
            hasValue(category) ||
            hasValue(subject) ||
            hasValue(creator) ||
            hasValue(assigneeLead) ||
            hasValue(assigneeGroup) ||
            hasValue(status)
        );
    }, [origin, dest, category, subject, creator, assigneeLead, assigneeGroup, status]);

    const clearAllFilters = useCallback(() => {
        setPage(1);
        setOriginState('');
        setDestState('');
        setCategoryState('');
        setSubjectState('');
        setCreatorState('');
        setAssigneeLeadState('');
        setAssigneeGroupState('');
        setStatusState('');
    }, [
        setOriginState,
        setDestState,
        setCategoryState,
        setSubjectState,
        setCreatorState,
        setAssigneeLeadState,
        setAssigneeGroupState,
        setStatusState,
    ]);

    // Always merge filters + applied searchParams and send to backend
    const toBackendValue = (v: any) => {
        const s = String(v ?? '').trim();
        if (!s) return '';
        if (s === ALL_VALUE) return '';
        if (s.toUpperCase() === 'ALL') return '';
        return s;
    };

    const toStoredValue = (v: any) => String(v ?? '').trim();

    const backendFilters = useMemo(
        () => ({
            origin: toBackendValue(origin),
            dest: toBackendValue(dest),
            category: toBackendValue(category),
            subject: toBackendValue(subject),
            creator: toBackendValue(creator),
            assigneeLead: toBackendValue(assigneeLead),
            assigneeGroup: toBackendValue(assigneeGroup),
            status: toBackendValue(status),
        }),
        [origin, dest, category, subject, creator, assigneeLead, assigneeGroup, status],
    );

    // Safety fallback: if filters change without wrapper setters, still reset to page=1 before fetching
    const filtersKey = useMemo(() => JSON.stringify(backendFilters), [backendFilters]);
    const prevFiltersKeyRef = useRef(filtersKey);

    // Any change in search/page/limit/sort/filters triggers the same server request
    useEffect(() => {
        if (tabKey !== 0) return;

        const prevKey = prevFiltersKeyRef.current;
        const filtersChanged = prevKey !== filtersKey;
        prevFiltersKeyRef.current = filtersKey;

        if (filtersChanged && page !== 1) {
            setPage(1);
            return;
        }

        const p: CarSearchParams = {
            ...(isSearchApplied ? searchParams : BASE_PARAMS),
            ...backendFilters,
        };

        void fetchServerPage(p, page, limit, sortBy, sortOrder);
    }, [tabKey, filtersKey, backendFilters, isSearchApplied, searchParams, page, limit, sortBy, sortOrder, fetchServerPage]);

    useEffect(() => {
        if (tabKey !== 0) return;
        if (didInitReportMetaRef.current) return;
        didInitReportMetaRef.current = true;

        void fetchAvgCompletionDaysFromApi();
        void fetchFilterOptionsFromApi();
    }, [tabKey, fetchAvgCompletionDaysFromApi, fetchFilterOptionsFromApi]);

    // If search input cleared after an applied search, clear applied search params
    useEffect(() => {
        const currHasAny = Boolean(uiSearchUFI.trim() || uiSearchComment.trim());
        const prevHasAny = prevUiHasAnyRef.current;
        prevUiHasAnyRef.current = currHasAny;

        if (!prevHasAny || currHasAny) return;
        if (!isSearchApplied) return;

        // clear applied search
        setSearchParams(BASE_PARAMS);

        // If there are no filters: Return to SERVER (server effect will pull the first page)
        if (!hasActiveFilters) setPage(1);
    }, [uiSearchUFI, uiSearchComment, isSearchApplied, hasActiveFilters]);

    // ----- pagination derived (server only) -----
    const displayTotalPages = useMemo(() => Math.max(1, totalPages), [totalPages]);
    const displayPage = Math.min(Math.max(1, page), displayTotalPages);

    useEffect(() => {
        if (page !== displayPage) setPage(displayPage);
    }, [page, displayPage]);

    const displayItems = useMemo(() => serverPageList, [serverPageList]);

    // ----- persist -----
    useLayoutEffect(() => {
        const appliedUFI = (searchParams?.ufi ?? '').trim();
        const appliedComment = (searchParams?.comment ?? '').trim();
        const didSearchNow = Boolean(appliedUFI || appliedComment);
        writePersistedCarListState({
            tabKey,
            page,
            limit,
            totalPages,

            lastSearchParams: { ufi: appliedUFI, comment: appliedComment },
            didSearch: didSearchNow,

            searchUFI: uiSearchUFI,
            searchComment: uiSearchComment,

            origin,
            dest,
            category,
            subject,
            creator,
            assigneeLead,
            assigneeGroup,
            status,

            sortBy,
            sortOrder,
        });
    }, [
        tabKey,
        page,
        limit,
        totalPages,
        searchParams,
        origin,
        dest,
        category,
        subject,
        creator,
        assigneeLead,
        assigneeGroup,
        status,
        sortBy,
        sortOrder,
        uiSearchUFI,
        uiSearchComment,
    ]);

    return (
        <>
            <div className={'CathaySansBd'} style={{ fontSize: '32px', lineHeight: '36px', marginBottom: 36 }}>
                CAR Report Tracker
            </div>

            <CNav variant="underline" role="tablist" style={{ marginBottom: 12 }}>
                {['Reports', 'Assignee'].map((label, idx) => (
                    <CNavItem key={label}>
                        <CNavLink
                            component="button"
                            style={{
                                fontSize: 16,
                                color: tabKey === idx ? '#116F9A' : '#4C4C4C',
                                borderBottom: tabKey === idx ? '3px solid #116F9A' : '3px solid transparent',
                                cursor: 'pointer',
                            }}
                            active={tabKey === idx}
                            onClick={() => setTabKey(idx)}
                        >
                            {label}
                        </CNavLink>
                    </CNavItem>
                ))}
            </CNav>

            <CTabContent>
                <CTabPane visible={tabKey === 0}>
                    <KpiSummary avgCompletionDays={avgCompletionDays} newTodayCount={newTodayCount} inProgressCount={inProgressCount} />

                    <div className="filtersRow" style={{ marginBottom: 32 }}>
                        <div className="filtersLeft">
                            <FiltersBar
                                searchUFI={uiSearchUFI}
                                setSearchUFI={setUiSearchUFI}
                                searchComment={uiSearchComment}
                                setSearchComment={setUiSearchComment}
                                origin={origin}
                                setOrigin={setOrigin}
                                originOptions={options.origin}
                                dest={dest}
                                setDest={setDest}
                                destOptions={options.dest}
                                category={category}
                                setCategory={setCategory}
                                categoryOptions={options.category}
                                subject={subject}
                                setSubject={setSubject}
                                subjectOptions={options.subject}
                                creator={creator}
                                setCreator={setCreator}
                                creatorOptions={options.creator}
                                assigneeLead={assigneeLead}
                                setAssigneeLead={setAssigneeLead}
                                assigneeLeadOptions={options.assigneeLead}
                                assigneeGroup={assigneeGroup}
                                setAssigneeGroup={setAssigneeGroup}
                                assigneeGroupOptions={options.assigneeGroup}
                                status={status}
                                setStatus={setStatus}
                                statusOptions={options.status}
                                onClearFilters={clearAllFilters}
                                showClearFilters={hasAnyFilterSelection}
                                dropdownWidth={200}
                                onSearch={async () => {
                                    const ufi = uiSearchUFI.trim();
                                    const comment = uiSearchComment.trim();
                                    const empty = !ufi && !comment;

                                    setPage(1);

                                    if (empty) {
                                        // Clear applied search
                                        setSearchParams(BASE_PARAMS);
                                        return;
                                    }

                                    const p: CarSearchParams = { ufi, comment };

                                    // Update applied searchParams only; fetching is triggered by unified effect
                                    setSearchParams(p);
                                }}
                                asAllOrValue={toStoredValue}
                            />
                        </div>

                        <div className="filtersRight">
                            <Access route={routePath} access={[AccessType.CREATE]}>
                                <CXButton
                                    outline
                                    text="Add new CAR report"
                                    onClick={() => {
                                        history.push(REPORTS_PATHS.carReportCreate);
                                    }}
                                    fontSize="14px"
                                    width="180px"
                                />
                            </Access>

                            <div className="limitTopRight" style={{ marginBottom: 4 }}>
                                <TablePagination
                                    limit={limit}
                                    totalPages={displayTotalPages}
                                    currentPage={displayPage}
                                    hidePages={true}
                                    hideLimit={false}
                                    onChangeLimit={(value) => {
                                        const nextLimit = Number(value);
                                        if (!Number.isFinite(nextLimit) || nextLimit <= 0) return;

                                        setLimit(nextLimit);
                                        writePersistedLimit(nextLimit);
                                        setPage(1);
                                    }}
                                    onChangePage={() => { }}
                                />
                            </div>
                        </div>
                    </div>

                    <CARReportsTable
                        items={displayItems}
                        onViewReport={onViewReport}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSortChange={(by, order) => {
                            setSortBy(by);
                            setSortOrder(order);
                            setPage(1);
                        }}
                    />

                    <TablePagination
                        limit={limit}
                        totalPages={displayTotalPages}
                        currentPage={displayPage}
                        hideLimit={true}
                        hidePages={false}
                        onChangeLimit={() => { }}
                        onChangePage={(value) => {
                            const next = Number(value);
                            if (!Number.isFinite(next) || next < 1 || next > displayTotalPages) return;
                            setPage(next);
                        }}
                    />
                </CTabPane>

                <CTabPane visible={tabKey === 1}>
                    {hasVisitedAssigneeTab ? <AssigneeTab /> : null}
                </CTabPane>
            </CTabContent>
        </>
    );
};

export default CARReportTrackerReportPage;
